import { useState, useRef, useMemo } from 'react';
import Image from 'next/image';
import NewSectionTitle from '@components/shared/NewSectionTitle';
import MonthSelector from './monthSelector';
import TestimonialSlider from '@components/local/placements/TestimonialSlider ';
import { BiDownload } from 'react-icons/bi';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import leftArrowIcon from '@assets/icons/placement-page/left-arrow.svg';
import rightArrowIcon from '@assets/icons/placement-page/right-arrow.svg';
import firstplaced from '@assets/icons/placement-page/first-placed.png';
import secondplaced from '@assets/icons/placement-page/second-placed.png';
import thirdplaced from '@assets/icons/placement-page/third-placed.png';
import fourthplaced from '@assets/icons/placement-page/fourth-placed.png';
import amazonLogo from '@assets/icons/placement-page/amazon-logo.svg';
import flower from '@assets/icons/placement-page/flower.svg';

const PlacementCarousel = ({ placementPage, onMonthChange, nonHeroStudentsData }) => {
  const isMobile = useMediaQuery('(max-width: 767.98px)');
  const scrollContainerRef = useRef(null);
  const availableMonths = Object.keys(nonHeroStudentsData);
  const [activeMonth, setActiveMonth] = useState(null);
  const [currentStudents, setCurrentStudents] = useState(() => {
    // Initially combine all students from all months
    return Object.values(nonHeroStudentsData)?.flat();
  });

  // Create a map of which month each student belongs to
  const monthIndexMap = useMemo(() => {
    const map = new Map();
    Object.entries(nonHeroStudentsData).forEach(([month, students]) => {
      students.forEach(student => {
        map.set(student.id, month);
      });
    });
    return map;
  }, [nonHeroStudentsData]);

  const mapToTestimonials = (rawData) => {
    return rawData.map((item, index) => ({
      id: item?.id || index + 1,
      name: item?.Name,
      position: item?.Role || 'Placed Candidate',
      quote: item?.HeroReview || item.Review || '',
      image: item?.Image || '',
      companyLogo: item?.company?.logo,
      tags: item?.Tags ? item?.Tags?.split(',') : [],
      month: monthIndexMap.get(item?.id) // Add month information to each testimonial
    }));
  };

  const handleMonthClick = (month) => {
    setActiveMonth(month);
    
    // Get the selected month's data
    const selectedMonthData = nonHeroStudentsData[month] || [];
    
    // Get data from all other months
    const otherMonthsData = Object.entries(nonHeroStudentsData)
      .filter(([key]) => key !== month)
      .flatMap(([_, data]) => data);
    
    // Combine selected month's data first, followed by other months' data
    const reorderedData = [...selectedMonthData, ...otherMonthsData];
    setCurrentStudents(reorderedData);
    
    // Reset scroll position to the first card
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }

    if (onMonthChange) onMonthChange(month);
  };

  const testimonialsdummy = mapToTestimonials(currentStudents);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      // Make scroll distance responsive to container width
      const scrollDistance = Math.max(200, containerWidth / 3);

      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollDistance : scrollDistance,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="-mt-24 max-w-6xl px-4 py-6 sm:px-6 sm:py-10 md:mx-auto md:px-8">
      <NewSectionTitle
        text={'See Who Got'}
        highlightedText={'Hired'}
        placementPage={placementPage}
      />
      <div className="relative mx-auto mb-0 mt-8 max-w-6xl overflow-hidden sm:px-4  md:mb-12 md:px-24 md:pb-4">
        <MonthSelector
          activeMonth={activeMonth}
          months={availableMonths}
          handleMonthClick={handleMonthClick}
          scroll={scroll}
          scrollContainerRef={scrollContainerRef}
        />
      </div>
      <div className="">
        <TestimonialSlider 
          testimonialsdummy={testimonialsdummy} 
          scrollContainerRef={scrollContainerRef}
          activeMonth={activeMonth}
          setActiveMonth={setActiveMonth}
          monthIndexMap={monthIndexMap}
        />
      </div>

      {/* Download report button */}
      <div className="mt-12 flex justify-center">
        <button className="flex w-fit items-center justify-center gap-x-4 rounded-[9px] bg-red p-3 font-gellix text-[14px] !normal-case text-white focus:outline-none md:font-proxima-nova md:text-lg">
          <BiDownload size={isMobile ? 18 : 24} />
          <span style={{ textWrap: 'nowrap' }} className="text-nowrap text-[14px] md:text-[18px]">
            Placement Report
          </span>
        </button>
      </div>
    </div>
  );
};

export default PlacementCarousel;
